/* eslint-disable max-lines */
import { Card, CardContent, Stack } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import { FlexListSummary } from "../../components/listSummary";
import { NoExperimentation } from "./NoExperimentation";
import { Link, useNavigate } from "react-router-dom";
import { DataTable } from "../../components/tables";
import { useState, useEffect } from "react";
import { ColorInsightTags, FilterTagWrapper } from "../../components/tags";
import { useQuery } from "@tanstack/react-query";
import styles from "./styles.module.scss";
import { getAllExperiments, getExperimentSummary, getWarehouseMetadata } from "../../helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import dayjs from "dayjs";
import {
  GET_EXPERIMENTATION,
  GET_EXPERIMENTATION_SUMMARY,
  GET_WAREHOUSE_METADATA,
} from "./constants";
import { Paginate } from "../../components/paginate";
import { useUsersWithServiceUserWithMapping } from "../../helpers/hooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { parseSortDirection } from "@/components/tables/types";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { FilterType } from "@/components/tables/types";
import { GetAllExperimentsParams, ExperimentItem } from "./types";

const Experimentation = () => {
  useBreadcrumb("Experimentation");
  const { searchParams, deleteSearchParamsByKeyValue, updateSearchParamsByKey } =
    useFiltersBySearchParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const userFilter = searchParams.getAll("userFilter") || [];
  const statusFilter = searchParams.getAll("statusFilter") || [];
  const entityFilter = searchParams.getAll("entityFilter") || [];
  const sortAttribute = searchParams.get("sortAttribute") || "";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");
  const { users, userNameToID, userIDtoName: _userIDtoName } = useUsersWithServiceUserWithMapping();
  // Convert number-keyed map to string-keyed for filter callbacks
  const userIDtoName: Record<string, string> = Object.fromEntries(
    Object.entries(_userIDtoName).map(([k, v]) => [String(k), v])
  );
  const [warehouseNameToRkMap, setWarehouseNameToRkMap] = useState<Record<string, string>>({});
  const [rkToWarehouseNameMap, setRkToWarehouseNameMap] = useState<Record<string, string>>({});

  const { data: experiments, isLoading } = useQuery({
    queryKey: [
      GET_EXPERIMENTATION,
      page,
      sortAttribute,
      sortOrder,
      ...userFilter,
      ...statusFilter,
      ...entityFilter,
      warehouseNameToRkMap,
      userNameToID,
    ],
    queryFn: () => {
      const params: GetAllExperimentsParams = {
        page: page + 1,
        size: 10,
        sort_key: sortAttribute || "",
        sort_order: sortOrder || "desc",
      };
      if (userNameToID && Object.keys(userNameToID).length && userFilter.length)
        params.users = userFilter.map(u => userNameToID[u]);
      if (statusFilter.length) params.status = statusFilter;
      if (warehouseNameToRkMap && Object.keys(warehouseNameToRkMap).length && entityFilter.length)
        params.entities = entityFilter.map(e => warehouseNameToRkMap[e]);
      return getAllExperiments(params);
    },
  });
  const { data: warehouseMetadata, isLoading: isWarehouseLoading } = useQuery({
    queryKey: [GET_WAREHOUSE_METADATA],
    queryFn: () => getWarehouseMetadata(),
  });

  // Handle successful warehouse metadata fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (warehouseMetadata) {
      const warehouseNameToRk: Record<string, string> = {};
      const rkToWarehouseName: Record<string, string> = {};
      warehouseMetadata.forEach(w => {
        warehouseNameToRk[w.warehouse_name] = w.rk;
        rkToWarehouseName[w.rk] = w.warehouse_name;
      });
      setWarehouseNameToRkMap(warehouseNameToRk);
      setRkToWarehouseNameMap(rkToWarehouseName);
    }
  }, [warehouseMetadata]);

  const { data: experimentSummary, isLoading: isSummaryLoading } = useQuery({
    queryKey: [GET_EXPERIMENTATION_SUMMARY],
    queryFn: () => getExperimentSummary(),
  });

  const onFilterUpdate = (filterName: string, fromData?: Record<string, string>) => {
    return (value: string[]) => {
      if (fromData) {
        updateSearchParamsByKey({
          [filterName]: value.map(v => fromData[v] ?? v),
        });
      } else {
        updateSearchParamsByKey({ [filterName]: value });
      }
    };
  };

  if (isLoading || isSummaryLoading || isWarehouseLoading) return <RelativeComponentLoader />;
  if (!experimentSummary) return null;
  if (experimentSummary.total_experiments === 0) return <NoExperimentation />;
  if (!experiments) return null;

  const experimentItems: ExperimentItem[] = experiments.items;

  return (
    <Stack direction="column" className="al-h-full">
      <Stack justify="between" align="center" className="mb-4">
        <h3 className="text-black">Experimentation</h3>
        <Link to="/experimentation/new">
          <Button>New Experimentation</Button>
        </Link>
      </Stack>
      <FlexListSummary
        summary={[
          {
            label: "Total Experiments",
            value: String(experimentSummary.total_experiments),
          },
          {
            label: "In Progress",
            value: String(experimentSummary.inprogress_experiments),
          },
          {
            label: "Success",
            value: String(experimentSummary.success_experiments),
          },
        ]}
      />

      <Card>
        <CardContent className="al-pt-4">
          <div className={styles.filter_tags}>
            <FilterTagWrapper
              filters={{
                statusFilter: {
                  filterStr: statusFilter,
                  label: "Status",
                  onclose: removedFilter => {
                    deleteSearchParamsByKeyValue({
                      statusFilter: removedFilter,
                    });
                  },
                },
                entityFilter: {
                  filterStr: entityFilter,
                  label: "Entity",
                  onclose: removedFilter =>
                    deleteSearchParamsByKeyValue({
                      entityFilter: removedFilter,
                    }),
                },
                userFilter: {
                  filterStr: userFilter,
                  label: "Users",
                  onclose: removedFilter => {
                    deleteSearchParamsByKeyValue({
                      userFilter: removedFilter,
                    });
                  },
                },
              }}
            />
          </div>
          <DataTable
            header={[
              {
                id: 1,
                label: "Experimentation Name",
                uid: "name",
                width: "18%",
                sort: {
                  sortSearchKey: "sortAttribute",
                },
                render: item => (
                  <div className="al-break-words al-text-brand al-cursor-pointer">{item.name}</div>
                ),
              },
              {
                id: 2,
                label: "Status",
                uid: "experiment_status",
                width: "6%",
                filter: {
                  filterType: FilterType.DROPDOWN,
                  value: statusFilter,
                  options: [
                    { label: "Planned", value: "Planned" },
                    { label: "In Progress", value: "In Progress" },
                    { label: "Completed", value: "Completed" },
                    { label: "Aborted", value: "Aborted" },
                  ],
                  onChange: onFilterUpdate("statusFilter"),
                },
                render: item => <div className={"al-break-words"}>{item.experiment_status}</div>,
              },
              {
                id: 3,
                label: "Owner",
                uid: "created_by",
                width: "6%",
                filter: {
                  filterType: FilterType.DROPDOWN,
                  value: userFilter,
                  options: (users || []).map(u => ({
                    label: `${u.first_name || ""} ${u.last_name || ""}`,
                    value: String(u.id),
                  })),
                  onChange: onFilterUpdate("userFilter", userIDtoName),
                },
                render: item => (
                  <div className={"al-break-words"}>
                    {users?.find(u => u.id === item.created_by)?.first_name}
                  </div>
                ),
              },
              {
                id: 4,
                label: "Period",
                uid: "period",
                width: "12%",
                render: item => (
                  <div className={"al-break-words"}>
                    {dayjs(item.experiment_start).format("DD MMM YYYY")}
                    {" - "}
                    {dayjs(item.experiment_end).format("DD MMM YYYY")}
                  </div>
                ),
              },
              {
                id: 5,
                label: "Insights",
                uid: "insights",
                width: "18%",
                render: item => (
                  <div className={"al-break-words"}>
                    {Array.from(
                      new Set(
                        (item.entities || [])
                          .map(e => e.insights || [])
                          .flat()
                          .map(i => i.tag_rk)
                      )
                    ).join(", ")}
                  </div>
                ),
              },
              {
                id: 6,
                label: "Entities",
                uid: "entities",
                width: "24%",
                filter: {
                  filterType: FilterType.DROPDOWN,
                  value: entityFilter,
                  options: (warehouseMetadata ?? []).map(w => ({
                    label: w.warehouse_name,
                    value: w.rk,
                  })),
                  onChange: onFilterUpdate("entityFilter", rkToWarehouseNameMap),
                },
                render: item => (
                  <div className={"al-break-words"}>
                    <ColorInsightTags
                      tags={(item.entities || []).map(e => e.entity_rk)}
                      labelFn={entityRk =>
                        (item.entities || []).find(e => e.entity_rk === entityRk)?.entity_name ??
                        entityRk
                      }
                    />
                  </div>
                ),
              },
              {
                id: 7,
                label: "Created On",
                uid: "created_on",
                width: "6%",
                render: item => (
                  <div className={"al-break-words"}>
                    {dayjs(item.created_on).format("DD MMM YYYY")}
                  </div>
                ),
              },
              {
                id: 8,
                label: "Start Time",
                uid: "experiment_start",
                width: "6%",
                render: item => (
                  <div className={"al-break-words"}>
                    {item.experiment_start
                      ? dayjs(item.experiment_start).format("DD MMM YYYY")
                      : "N/A"}
                  </div>
                ),
              },
            ]}
            data={{ items: experimentItems }}
            idKey="id"
            onRowClick={item => navigate(`/experimentation/${item.id}`)}
          />
          <Paginate
            itemCount={experiments.total}
            page={page}
            pageSize={experiments.size}
            numPages={experiments.pages}
            onPageClick={setPage}
            onPageSizeChange={() => {}}
          />
        </CardContent>
      </Card>
    </Stack>
  );
};

export { Experimentation };
